<template>
    <div class="common-marker">
        <div ref="markerTriangleRef" class="common-marker_triangle" :class="{isReduced}">
            <VJoyIcon v-if="!isReduced" class="common-marker_triangle_icon" :name="icon" color="white" :size="iconSize" />
            <div class="common-marker_triangle_content">
                <div class="common-marker_triangle_content_icon">
                    <VJoyTooltip>
                        <div class="common-marker_triangle_content_icon_wrapper" @click="toggleDisplay">
                            <VJoyIcon :name="displayedIcon" size="xsmall" color="white" />
                        </div>
                        <template #tooltip-content>
                            {{ isReduced ? 'Display environnement indicators' : 'Disable environnement indicators' }}
                        </template>
                    </VJoyTooltip>
                </div>
                <div class="common-marker_triangle_content_text">
                    <div class="common-marker_triangle_content_text_title">
                        <slot />
                        mode
                    </div>
                    <div class="common-marker_triangle_content_text_description">
                        <slot name="description" />
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div v-if="!isReduced" class="common-marker-border"></div>
</template>
<script setup lang="ts">
    import {computed} from 'vue';
    import {useLocalStorage} from '@vueuse/core';
    import {useMediaQuery} from '@malt/utils';
    import {VJoyIcon, VJoyTooltip, type TJoyIconSizes} from '@maltjoy/core-vue';
    import type {JoyIconsId} from '@maltjoy/icons';

    defineProps<{
        /**
         * The background color of the marker
         */
        color: string;
        /**
         * The icon displayed in the corner
         */
        icon: JoyIconsId;
    }>();

    const isReduced = useLocalStorage('environnement-marker-displayed', false);

    const {matchMedia} = useMediaQuery();

    const displayedIcon = computed<JoyIconsId>(() => (isReduced.value ? 'eye' : 'eye-masked'));
    const iconSize = computed<TJoyIconSizes>(() => (matchMedia.value.max.Sm ? 'xxsmall' : 'xsmall'));

    function toggleDisplay() {
        isReduced.value = !isReduced.value;
    }

    defineExpose({
        isReduced,
        displayedIcon,
    });
</script>
<style scoped lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .common-marker {
        position: fixed;
        display: flex;
        flex-flow: row nowrap;
        top: 0;
        right: 0;
        // Above dialogs and funnel, but below tooltips
        z-index: calc(var(--joy-core-z-index-dialog) + 1);

        &_triangle {
            width: 40px;
            height: 40px;
            border-bottom-left-radius: 100%;
            aspect-ratio: 1;
            position: relative;
            display: inline-block;
            background: v-bind(color);
            corner-shape: superellipse(0.5);
            transition-property: width, height, border-bottom-left-radius;
            transition-duration: var(--joy-transition-duration-short);
            transition-timing-function: ease-out;
            box-shadow: var(--joy-core-elevation-2);
            overflow: hidden;
            cursor: pointer;
            $p: &;

            &.isReduced {
                width: 10px;
                height: 10px;
            }

            &:hover {
                width: 250px;
                height: 70px;
                border-bottom-left-radius: var(--joy-core-radius-3);
                corner-shape: round;

                #{$p} {
                    &_icon {
                        opacity: 0;
                    }
                    &_content {
                        opacity: 1;

                        &_icon {
                            margin-top: 0;
                        }
                    }
                }
            }

            &_icon {
                position: absolute;
                top: 15px;
                right: 0px;
                transform: translate(-50%, -50%) rotate(45deg);
                transition: opacity var(--joy-transition-duration-default) ease-out;
            }

            @include mq.screen_sm() {
                width: 30px;
                height: 30px;

                #{$p}_icon {
                    top: 12px;
                    right: 0px;
                }
            }

            &_content {
                display: flex;
                flex-flow: row nowrap;
                justify-content: center;
                align-items: center;
                gap: var(--joy-core-spacing-2);
                height: 100%;
                opacity: 0;
                transition: opacity var(--joy-transition-duration-default) ease-out;
                padding: var(--joy-core-spacing-2);
                font-size: var(--joy-font-size-primary-300);
                text-align: center;

                &_icon {
                    padding: 0 var(--joy-core-spacing-1);
                    // Prevent a bug on mobile where menu is clicked when hovering
                    transition: margin-top 0.05s ease-out;
                    margin-top: -100px;

                    &_wrapper {
                        padding: var(--joy-core-spacing-2);
                        display: flex;
                        border-radius: 100%;

                        &:hover {
                            background-color: rgba(255, 255, 255, 0.3);
                        }

                        &:active {
                            background-color: rgba(255, 255, 255, 0.2);
                        }
                    }
                }

                &_text {
                    display: flex;
                    flex-flow: column nowrap;
                    justify-content: center;
                    gap: var(--joy-core-spacing-1);
                    overflow: hidden;
                    padding-right: var(--joy-core-spacing-2);

                    &_title {
                        white-space: nowrap;
                        background-color: white;
                        border-radius: var(--joy-core-radius-3);
                        padding: 2px var(--joy-core-spacing-2);
                        font-size: var(--joy-font-size-primary-200);
                        text-align: center;
                        text-transform: uppercase;
                        font-weight: var(--joy-font-weight-bold);
                    }

                    &_description {
                        font-size: var(--joy-font-size-primary-200);
                        color: var(--joy-color-neutral-20);
                        width: 175px;
                        white-space: pre-wrap;
                    }
                }
            }
        }
    }
    .common-marker-border {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: calc(var(--joy-core-z-index-dialog) + 1);
        border: 4px solid var(--joy-color-brand-primary-70);
        pointer-events: none;
    }
</style>
